import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, throwError, tap } from 'rxjs';
import { LoggingService } from './logging.service';
import { ApiService } from './api.service';
import { TenantRouterService } from '../services/tenant-router.service';

interface AdminLoginResponse {
  access_token: string;
}

export interface AppUser {
  UserID: number;
  Name: string;
  Email: string;
  IsActive: boolean;
  personType: 'user' | 'manager' | 'hr';
}


@Injectable({
  providedIn: 'root'
})
export class AuthService {

  // BehaviorSubject to track authentication status
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasValidToken());
  isAuthenticated$ = this.isAuthenticatedSubject.asObservable(); // Exposed as observable for components to subscribe
  private currentUserSubject = new BehaviorSubject<AppUser | null>(this.getStoredUser());
  currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private loggingService: LoggingService,
    private apiService: ApiService,  private tenantRouter: TenantRouterService
  ) {}

  /** Attempt login and store token on success */
  login(data: { Email: string; Password: string; IsActive: boolean }): Observable<AdminLoginResponse> {
    data.IsActive = true;
    this.loggingService.logInfo("Authenticating the user to get access token with email and password");

    return this.httpClient.post<AdminLoginResponse>(this.apiService.getUrl('user/login'), data).pipe(
      tap((response: AdminLoginResponse) => {
        if (response?.access_token) {
          sessionStorage.setItem('personSessionToken', response.access_token);
          // You may also store user info here if needed
          this.isAuthenticatedSubject.next(true); // Emit login success
        } else {
          throw new Error("Login succeeded but token is missing");
        }
      }),
      catchError(error => {
        this.loggingService.logError("Login failed", error);
        return throwError(() => new Error("Login failed. Please try again."));
      })
    );
  }

   //Request forgot password link 
forgotPassword(email: string): Observable<any> {
  this.loggingService.logInfo("Requesting forgot password link for " + email);

  return this.httpClient.post<any>(
    this.apiService.getUrl('user/forgotPassword'), { EmailId: email } ).pipe(
    tap((res) => {
      this.loggingService.logInfo("Forgot password request successful", res);
    }),
    catchError(error => {
      this.loggingService.logError("Forgot password request failed", error);
      return throwError(() => error);
    })
  );
}


  // Called during login success
  setCurrentUser(user: AppUser) {
    sessionStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  /** Logout: clear session and emit logged out status */
  logout(): void {
    sessionStorage.removeItem('personSessionToken');
    sessionStorage.removeItem('user_role');
    sessionStorage.removeItem('manager_id');
    sessionStorage.removeItem('user_id');
    sessionStorage.removeItem('assignment_id');
    sessionStorage.removeItem('currentUser');
    this.currentUserSubject.next(null);

    this.loggingService.logInfo("User logged out and session cleared.");
    this.isAuthenticatedSubject.next(false); // Emit logout
  }

  /** Check if the user is authenticated */
  isAuthenticated(): boolean {
    return this.hasValidToken();
  }

  /** Get the token */
  getSessionToken(): string | null {
    return sessionStorage.getItem('personSessionToken');
  }

  /** Get the user's role */
  getUserRole(): string | null {
    return sessionStorage.getItem('user_role');
  }

  /** Internal check for token validity */
  private hasValidToken(): boolean {
    const token = sessionStorage.getItem('personSessionToken');
    return !!token;
    // Optional: add expiry check logic if your token has expiration
  }

  private getStoredUser(): AppUser | null {
    const user = sessionStorage.getItem('currentUser');
    return user ? JSON.parse(user) : null;
  }
}
